/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { useEffect, useRef, useState } from "react"
import { useIntersectionObserver } from "hooks/useIntersectionObserver"
import { useTranslation } from "common/utils/i18n"
import { jsx, css } from "@emotion/core"
import { Spin } from "@duxton-web/ui"

const loadingMoreStyle = css`
  padding: 10px;
  color: #979797;
`

const LoadMore = ({
  onLoadMore,
  component: Component,
}: {
  onLoadMore: () => void
  component?: React.ReactNode
}) => {
  const [hasLoadedMore, setHasLoadedMore] = useState(false)
  const lastItemRef = useRef<HTMLDivElement | null>(null)
  const [scrollRoot, setScrollRoot] = useState<Element | null | undefined>(
    undefined,
  )

  // Find the scroll container (react-window List or Table scroll container)
  const findScrollContainer = (element: HTMLElement | null) => {
    if (!element) return null

    let current: Element | null = element.parentElement

    // Traverse up the DOM tree to find the scroll container
    // react-window creates a scrollable div, or we might be in a Table with scroll
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current)
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll"

      // Check if this element is scrollable and has a height constraint
      if (hasScroll && current.scrollHeight > current.clientHeight) {
        return current
      }

      current = current.parentElement
    }

    return null
  }

  // Use callback ref to find scroll container when element is mounted
  const setLastItemRef = (element: HTMLDivElement | null) => {
    lastItemRef.current = element
    if (element && scrollRoot === undefined) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        const container = findScrollContainer(element)
        setScrollRoot(container)
      })
    }
  }

  // Observer options - wait until scrollRoot is determined (even if null)
  const observerOptions =
    scrollRoot !== undefined
      ? {
          threshold: 0,
          root: scrollRoot,
          rootMargin: "1000px",
        }
      : {
          threshold: 1,
          rootMargin: "1000px",
        }

  const entry = useIntersectionObserver(lastItemRef, observerOptions)
  const isVisible = !!entry?.isIntersecting

  useEffect(() => {
    if (isVisible && !hasLoadedMore) {
      onLoadMore()
      setHasLoadedMore(true)
    } else if (!isVisible && hasLoadedMore) {
      setHasLoadedMore(false)
    }
  }, [isVisible, onLoadMore, hasLoadedMore])

  const loadingMoreText = useTranslation("common.loadingMore")

  if (Component) {
    return <div ref={setLastItemRef}>{Component}</div>
  }

  return (
    <div ref={setLastItemRef} css={loadingMoreStyle}>
      {loadingMoreText} <Spin size="small" />
    </div>
  )
}

export default LoadMore
